/**
 * Шторка кошелька: пополнение и вывод в iframe-виджете брокера.
 *
 * Ссылок на депозит у брокера нет — деньги вносятся в его виджете, встроенном сюда.
 * Сессию виджета выпускает наш бэкенд (живёт минуту, одноразовая) и она уходит
 * в iframe через postMessage; в адресе iframe нет ни пользователя, ни токена.
 * Рукопожатие и проверки origin — в `lib/walletBridge.ts`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, apiMessage, ApiError } from '../lib/api.js';
import type { WalletSessionResponse } from '../lib/api.js';
import type { WalletRequest } from '../lib/deepLink.js';
import { haptic } from '../lib/telegram.js';
import { createWalletBridge, MIN_WIDGET_HEIGHT } from '../lib/walletBridge.js';
import { useApp } from '../state/app.js';
import { SheetPortal } from './SheetPortal.js';

export function WalletSheet({ request, onClose }: { request: WalletRequest; onClose: () => void }) {
  const { t, refresh, showToast, setTab } = useApp();
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [info, setInfo] = useState<WalletSessionResponse | null>(null);
  const [height, setHeight] = useState(MIN_WIDGET_HEIGHT + 100);
  const [failed, setFailed] = useState<string | null>(null);

  // первая сессия: вместе с ней сервер отдаёт адрес и origin виджета
  useEffect(() => {
    let cancelled = false;
    setInfo(null);
    setFailed(null);
    api
      .walletSession({ mode: request.mode, packageId: request.packageId, stage: request.stage })
      .then((result) => {
        if (!cancelled) setInfo(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const code =
          err instanceof ApiError && err.payload && typeof err.payload === 'object'
            ? String((err.payload as { error?: unknown }).error ?? '')
            : '';
        // чужой аккаунт или нет привязки: бонусы не привяжутся — ведём на экран аккаунта,
        // где можно выйти и создать новый (репорт заказчика базы 2026-08-23)
        // и вход через другой бот: касса откроется только после повторного входа здесь
        if (code === 'foreign_account' || code === 'account_required' || code === 'relogin_required') {
          showToast(apiMessage(err, t, 'app.tokens.broker_silent'));
          setTab('account');
          onClose();
          return;
        }
        setFailed(apiMessage(err, t, 'app.tokens.broker_silent'));
      });
    return () => {
      cancelled = true;
    };
  }, [request.mode, request.packageId, request.stage]);

  const bridge = useMemo(() => {
    if (!info) return null;
    const created = createWalletBridge({
      embedOrigin: info.embedOrigin,
      eventPrefix: info.eventPrefix,
      frameWindow: () => frameRef.current?.contentWindow ?? null,
      mintSession: (renew) =>
        api.walletSession({ mode: request.mode, packageId: request.packageId, stage: request.stage, renew }),
      onCredited: () => {
        haptic('medium');
        // суммам из события не верим: сервер сверяет платежи у брокера и сам начисляет токены
        void api
          .walletSync(request.stage, true)
          .then((result) => {
            showToast(t(result.deposits > 0 ? 'app.wallet.credited' : 'app.wallet.processing'));
          })
          .catch(() => showToast(t('app.wallet.processing')))
          .finally(() => void refresh());
      },
      onClose,
      onResize: setHeight,
      onError: (message) => setFailed(message),
    });
    created.primeSession(info.session);
    return created;
    // колбэки намеренно не в зависимостях: мост живёт столько же, сколько сессия виджета
  }, [info, request.mode, request.packageId, request.stage]);

  useEffect(() => {
    if (!bridge) return;
    const listener = (event: MessageEvent): void => {
      bridge.handle({ origin: event.origin, source: event.source, data: event.data });
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [bridge]);

  // закрыли шторку — балансы могли измениться (вывод события `credited` не шлёт)
  const close = (): void => {
    void api.walletSync(request.stage).catch(() => undefined).finally(() => void refresh());
    onClose();
  };

  return (
    <SheetPortal>
      <div
        className="sheet-backdrop"
        onClick={(event) => {
          if (event.target === event.currentTarget) close();
        }}
      >
        <div className="sheet sheet-wallet">
          <div className="sheet-handle" />
          <div className="sheet-wallet-head">
            <h2 className="card-title">
              {t(request.mode === 'deposit' ? 'app.wallet.deposit_title' : 'app.wallet.withdraw_title')}
            </h2>
            <button type="button" className="chip" onClick={close}>
              <span className="chip-label">{t('app.wallet.close')}</span>
            </button>
          </div>

          {failed ? (
            <p className="body">{failed}</p>
          ) : info ? (
            <iframe
              ref={frameRef}
              className="wallet-frame"
              title={t(request.mode === 'deposit' ? 'app.wallet.deposit_title' : 'app.wallet.withdraw_title')}
              src={info.embedUrl}
              style={{ height }}
              allow="payment *; clipboard-write *"
              referrerPolicy="no-referrer"
            />
          ) : (
            <p className="body ai-shimmer">{t('app.wallet.loading')}</p>
          )}
        </div>
      </div>
    </SheetPortal>
  );
}
